import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { adminUrl } from '../lib/apps';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { Badge, Detail, Field, Input, Select, Textarea, useToast } from '../components/ui';
import type { SysAccessRow } from './Access';

interface SysRole {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  description: string | null;
  permissions: string[];
  holders: number;
}

interface Candidate {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  email: string;
  employeeCode: string | null;
  jobTitle: string | null;
  departmentMemberships: Array<{ department: { id: string; code: string; name: string; nameAr: string | null } }>;
}

/** The server explains itself in English; the one refusal people will meet here reads in their language. */
function useAccessError() {
  const { t } = useI18n();
  return (e: unknown) =>
    e instanceof ApiError && e.code === 'LAST_SYS_ADMIN' ? t('access.lastAdmin') : (e as Error).message;
}

/**
 * Give an employee access to sys, or change the sys roles of someone who has it.
 *
 * Routes: `/access/new` and `/access/:userId/edit`. The person always comes from
 * the admin — this page picks an existing employee and never creates one.
 */
export function AccessFormPage() {
  const { userId } = useParams();
  const editing = !!userId;
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const explain = useAccessError();

  const [search, setSearch] = useState('');
  const [personId, setPersonId] = useState(userId ?? '');
  const [roleCodes, setRoleCodes] = useState<string[]>([]);
  const [prefilled, setPrefilled] = useState(!editing);

  const roles = useQuery({
    queryKey: ['sys-access', 'roles'],
    queryFn: () => api.get<SysRole[]>('/sys-access/roles'),
  });
  const person = useQuery({
    queryKey: ['sys-access', 'person', userId],
    queryFn: () => api.get<SysAccessRow>(`/sys-access/${userId}`),
    enabled: editing,
  });
  const candidates = useQuery({
    queryKey: ['sys-access', 'candidates', search],
    queryFn: () => api.get<Candidate[]>('/sys-access/candidates', { q: search.trim() || undefined }),
    enabled: !editing,
  });

  useEffect(() => {
    if (prefilled || !person.data) return;
    setRoleCodes(person.data.sysRoles.map((r) => r.code));
    setPrefilled(true);
  }, [prefilled, person.data]);

  const save = useMutation({
    mutationFn: () => api.put(`/sys-access/${personId}`, { roleCodes }),
    onSuccess: () => {
      toast.success(t('access.saved'));
      void queryClient.invalidateQueries({ queryKey: ['sys-access'] });
      navigate('/access');
    },
    onError: (e) => toast.error(explain(e)),
  });

  const toggle = (code: string, on: boolean) =>
    setRoleCodes((codes) => (on ? [...codes, code] : codes.filter((c) => c !== code)));

  const roleHint = (role: SysRole) => {
    const key = `access.role.${role.code}` as TranslationKey;
    const hint = t(key);
    // A role added later without a translation falls back to its own description.
    return hint === key ? role.description ?? '' : hint;
  };

  return (
    <FormPage
      title={editing ? t('access.editTitle') : t('access.grantTitle')}
      subtitle={editing && person.data ? localized(person.data) : undefined}
      backTo="/access"
      backLabel={t('access.title')}
      width="lg"
      submitLabel={editing ? t('common.save') : t('access.grant')}
      submitting={save.isPending}
      disabled={!personId || roleCodes.length === 0}
      loading={roles.isLoading || (editing && person.isLoading)}
      error={roles.error ?? person.error}
      onRetry={() => {
        void roles.refetch();
        if (editing) void person.refetch();
      }}
      onSubmit={() => save.mutate()}
    >
      {editing ? (
        person.data && (
          <dl className="mb-5 divide-y divide-[var(--line)]">
            <Detail label={t('access.employee')}>{localized(person.data)}</Detail>
            <Detail label={t('common.email')}>
              <span dir="ltr">{person.data.email}</span>
            </Detail>
            <Detail label={t('access.since')}>
              {person.data.accessSince ? formatDate(person.data.accessSince, locale) : '—'}
            </Detail>
          </dl>
        )
      ) : (
        <div className="mb-5 grid gap-4 sm:grid-cols-2">
          <Field label={t('common.search')}>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </Field>
          <Field
            label={t('access.employee')}
            required
            hint={
              <span>
                {t('access.employeeHint')}{' '}
                <a
                  href={`${adminUrl()}/employees/new`}
                  className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t('access.createInAdmin')}
                  <ExternalLink className="h-3 w-3 flip-rtl" aria-hidden />
                </a>
              </span>
            }
          >
            <Select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">{candidates.data?.length === 0 ? t('access.noCandidates') : '—'}</option>
              {(candidates.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {localized(c)}
                  {c.employeeCode ? ` · ${c.employeeCode}` : ''}
                  {c.departmentMemberships[0] ? ` · ${localized(c.departmentMemberships[0].department)}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      <fieldset>
        <legend className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">
          {t('access.roles')} <span className="text-primary dark:text-primary-400">*</span>
        </legend>
        <p className="mb-3 text-caption text-faint">{t('access.rolesHint')}</p>
        <div className="grid gap-3 md:grid-cols-3">
          {(roles.data ?? []).map((role) => {
            const on = roleCodes.includes(role.code);
            return (
              <label
                key={role.code}
                className={clsx(
                  'flex cursor-pointer flex-col gap-2 rounded-md border p-4 transition-colors duration-fast',
                  on ? 'border-primary bg-primary/8' : 'hairline hover:bg-[var(--surface-sunken)]',
                )}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={on}
                    onChange={(e) => toggle(role.code, e.target.checked)}
                  />
                  <span className="text-title text-[var(--text-primary)]">
                    {locale === 'ar' ? role.nameAr ?? role.name : role.name}
                  </span>
                </span>
                <span className="text-body-sm text-muted">{roleHint(role)}</span>
                <Badge tone="neutral" className="self-start tabular">
                  {t('access.permissions', { count: role.permissions.length })}
                </Badge>
              </label>
            );
          })}
        </div>
      </fieldset>
    </FormPage>
  );
}

/**
 * Take someone's sys access away. Their sys session ends immediately; their
 * admin account is untouched.
 *
 * Route: `/access/:userId/revoke`.
 */
export function AccessRevokePage() {
  const { userId = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const explain = useAccessError();
  const [reason, setReason] = useState('');

  const person = useQuery({
    queryKey: ['sys-access', 'person', userId],
    queryFn: () => api.get<SysAccessRow>(`/sys-access/${userId}`),
  });

  const revoke = useMutation({
    mutationFn: () => api.post(`/sys-access/${userId}/revoke`, { reason: reason.trim() || undefined }),
    onSuccess: () => {
      toast.success(t('access.revoked'));
      void queryClient.invalidateQueries({ queryKey: ['sys-access'] });
      navigate('/access');
    },
    onError: (e) => toast.error(explain(e)),
  });

  return (
    <FormPage
      title={t('access.revokeTitle')}
      subtitle={person.data ? localized(person.data) : undefined}
      backTo="/access"
      backLabel={t('access.title')}
      submitLabel={t('access.revoke')}
      destructive
      submitting={revoke.isPending}
      loading={person.isLoading}
      error={person.error}
      onRetry={() => void person.refetch()}
      summary={
        person.data && (
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('access.employee')}>{localized(person.data)}</Detail>
            <Detail label={t('access.roles')}>
              <span className="inline-flex flex-wrap justify-end gap-1.5">
                {person.data.sysRoles.map((role) => (
                  <Badge key={role.code} tone="neutral">
                    {locale === 'ar' ? role.nameAr ?? role.name : role.name}
                  </Badge>
                ))}
              </span>
            </Detail>
          </dl>
        )
      }
      onSubmit={() => revoke.mutate()}
    >
      <p className="mb-4 rounded-md border border-warning/30 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
        {t('access.revokeNote')}
      </p>
      <Field label={t('common.reason')} hint={t('common.optional')}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
      </Field>
    </FormPage>
  );
}
